import { useQuery } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell, EmptyState } from './card-shell';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function RecentClientsCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', 'recent', { pageSize: 5 }],
    queryFn: () =>
      apiClient.clientsList({
        pageSize: 5,
        sortBy: 'createdAt',
        sortDir: 'desc',
      }),
    staleTime: 1000 * 60,
  });

  if (isLoading) {
    return (
      <CardShell title="Últimos clientes" icon={UserPlus}>
        <div className="flex w-full flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-8 w-full animate-pulse rounded bg-[var(--surface-muted)]"
            />
          ))}
        </div>
      </CardShell>
    );
  }

  if (isError) {
    return (
      <CardShell title="Últimos clientes" icon={UserPlus}>
        <p className="text-xs text-[var(--fg-muted)]">
          Não foi possível carregar.
        </p>
      </CardShell>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <CardShell title="Últimos clientes" icon={UserPlus}>
        <EmptyState
          icon={UserPlus}
          message="Nenhum cliente cadastrado ainda. Comece adicionando o primeiro."
          cta={{ label: '+ Novo cliente', href: '/escritorio/clientes/novo' }}
        />
      </CardShell>
    );
  }

  return (
    <CardShell title="Últimos clientes" icon={UserPlus}>
      <div className="flex w-full flex-col gap-1">
        <ul className="flex flex-col divide-y divide-[var(--border)]">
          {items.map((c) => (
            <li key={c.id}>
              <Link
                to={`/escritorio/clientes/${c.id}`}
                className={cn(
                  'flex items-center justify-between gap-3 rounded px-1 py-1.5 text-xs transition',
                  'hover:bg-[var(--surface-muted)]',
                )}
              >
                <span className="truncate text-[var(--fg)]">{c.name}</span>
                <span className="shrink-0 text-[10px] text-[var(--fg-muted)]">
                  {formatDate(c.createdAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/escritorio/clientes"
          className={cn(
            'mt-2 inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition',
            'border border-[var(--border)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
          )}
        >
          Ver todos
        </Link>
      </div>
    </CardShell>
  );
}
